<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOverlay } from '@/composables/useOverlay'

/**
 * Fenêtre modale : une décision courte qui interrompt.
 *
 * Pour un travail « à côté » du contenu — éditer une fiche, consulter un
 * détail, filtrer — c'est `AppDrawer` qu'il faut. Un formulaire de quinze
 * champs dans une modale est un tiroir mal nommé.
 *
 * Tout le comportement (Échap, clic sur le fond avec la subtilité du geste
 * commencé DANS la boîte, blocage du défilement de l'arrière-plan, retour du
 * focus à son point de départ) vient de `useOverlay`, partagé avec le tiroir.
 * Ces quatre points sont ceux qu'on oublie en réécrivant une modale à la main —
 * les factoriser est la seule façon de ne pas les réoublier.
 */

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    /** Largeur maximale de la boîte. Par défaut : un formulaire confortable. */
    width?: string
    /**
     * Corps SANS rembourrage, pour un contenu qui pose sa propre mise en page.
     *
     * Nécessaire dès qu'un panneau veut une colonne latérale : elle doit toucher
     * les bords de la boîte, sinon on voit une bande de fond entre la bordure et
     * la colonne, et le repère visuel « ceci est un rail » disparaît.
     */
    flush?: boolean
    /** Hauteur fixe, pour un panneau à colonne latérale qui ne doit pas sauter
     *  de taille en changeant de section. */
    height?: string
  }>(),
  { width: '520px', flush: false, height: undefined },
)

const emit = defineEmits<{ close: [] }>()

// `t()` ne sert qu'au bouton de fermeture — le titre reste une PROP : la
// traduction d'un contenu métier appartient à l'écran qui l'ouvre.
const { t } = useI18n()

const { box, onBackdropMousedown, onBackdropClick } = useOverlay({
  open: computed(() => props.open),
  onClose: () => emit('close'),
})
</script>

<template>
  <Transition name="modal">
    <div
      v-if="open"
      class="overlay"
      :class="{ 'overlay--hauteur-fixe': height !== undefined }"
      @mousedown="onBackdropMousedown"
      @click="onBackdropClick"
    >
      <div
        ref="box"
        class="modal card"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        :style="{ maxWidth: width, height }"
        tabindex="-1"
      >
        <header class="modal-head">
          <h2 class="modal-title">{{ title }}</h2>
          <button
            class="btn btn-ghost btn-icon"
            type="button"
            data-overlay-close
            :aria-label="t('common.close')"
            @click="emit('close')"
          >
            ×
          </button>
        </header>

        <div class="modal-body" :class="{ 'modal-body--flush': flush }">
          <slot />
        </div>

        <footer v-if="$slots.actions" class="modal-foot">
          <slot name="actions" />
        </footer>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay-backdrop);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  /* Alignée en haut et non centrée : une modale centrée qui grandit pousse ses
     boutons hors de l'écran sur un portable. Ici elle grandit vers le bas, et
     le conteneur défile. */
  padding: var(--space-12) var(--space-4);
  background: var(--color-backdrop);
  overflow-y: auto;
}

/* Hauteur FIXE : la boîte ne grandit plus, l'argument de l'alignement en haut
   tombe. Gardé en haut, le rembourrage de 48 px s'ajoutait à la hauteur et une
   modale à 95vh sortait de l'écran par le bas (constaté). Centrée, elle tient
   dans la fenêtre, et `max-height` la rattrape si la hauteur demandée excède
   l'espace réel (barre d'adresse mobile). */
.overlay--hauteur-fixe {
  align-items: center;
  padding-block: var(--space-2);
}

/* Colonne flex : sans elle, `.modal-body--flush { flex: 1 }` ne s'applique à
   rien, le corps garde la hauteur de son contenu et une colonne latérale
   s'arrête à mi-hauteur de la boîte (constaté). */
.overlay--hauteur-fixe .modal {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}

.modal {
  position: relative;
  z-index: var(--z-modal);
  width: 100%;
  background: var(--color-bg-elevated);
  box-shadow: var(--shadow-xl);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  border-bottom: 1px solid var(--color-border);
}

.modal-title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
}

.modal-body {
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* APRÈS `.modal-body`, et c'est tout le point : à spécificité égale, c'est la
   dernière règle déclarée qui gagne. Placé avant, ce bloc était intégralement
   annulé par le rembourrage ci-dessus — la modale de réglages gardait son
   espacement et le rail ne touchait pas les bords. */
.modal-body--flush {
  padding: 0;
  /* Le contenu prend toute la hauteur restante : c'est ce qui permet au rail de
     descendre jusqu'en bas de la boîte au lieu de s'arrêter au texte. */
  flex: 1;
  min-height: 0;
  gap: 0;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-border);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--duration-fast) var(--ease-out);
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform var(--duration-fast) var(--ease-out);
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: translateY(-8px);
}
</style>
